import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import {
  createEnvironmentMaterials,
  disposeMaterialSet,
  type EnvironmentMaterialSet,
} from './materials';
import {
  DEFAULT_PLOT_DIMENSIONS,
  createProceduralEnvironment,
  createSharedEnvironmentGeometry,
  disposeSharedEnvironmentGeometry,
  type PlotDimensions,
  type SharedEnvironmentGeometry,
} from './procedural';
import {
  clampPixelRatio,
  getQualityProfile,
  type QualityProfile,
  type QualityTier,
} from './quality';
import { SceneRootManager, type ReplaceRootOptions } from './roots';
import {
  DEFAULT_ENVIRONMENT_THEME_ID,
  getEnvironmentTheme,
  type EnvironmentThemeDefinition,
  type EnvironmentThemeId,
} from './themes';

export interface RendererLike {
  readonly domElement: HTMLCanvasElement;
  readonly shadowMap: { enabled: boolean; type: THREE.ShadowMapType };
  outputColorSpace: string;
  toneMapping: THREE.ToneMapping;
  toneMappingExposure: number;
  setPixelRatio(value: number): void;
  setSize(width: number, height: number, updateStyle?: boolean): void;
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  dispose(): void;
}

export type RendererFactory = (parameters: THREE.WebGLRendererParameters) => RendererLike;

export interface BunchaSceneOptions {
  readonly canvas: HTMLCanvasElement;
  readonly qualityTier?: QualityTier;
  readonly themeId?: EnvironmentThemeId;
  readonly plot?: PlotDimensions;
  readonly rendererFactory?: RendererFactory;
}

interface SkyResources {
  readonly sky: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  readonly environment: THREE.Texture;
  dispose(): void;
}

function createSkyResources(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  theme: EnvironmentThemeDefinition,
): SkyResources {
  const skyMaterial = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      topColor: { value: new THREE.Color(theme.colors.skyTop) },
      horizonColor: { value: new THREE.Color(theme.colors.skyHorizon) },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 horizonColor;
      varying vec3 vWorldPosition;
      void main() {
        float h = clamp(normalize(vWorldPosition).y * 0.72 + 0.38, 0.0, 1.0);
        gl_FragColor = vec4(mix(horizonColor, topColor, smoothstep(0.0, 1.0, h)), 1.0);
      }
    `,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(450, 32, 18), skyMaterial);
  sky.name = `sky:${theme.id}`;
  sky.renderOrder = -100;

  // Keep the visible gradient independent from image-based lighting. Capturing
  // an analytical Sky into PMREM caused the iOS black-material regression;
  // RoomEnvironment is Three.js's documented IBL source and the Phase 0 path.
  const lightingEnvironment = new RoomEnvironment();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environmentTarget = pmrem.fromScene(lightingEnvironment, 0.04);
  lightingEnvironment.dispose();
  scene.add(sky);
  scene.environment = environmentTarget.texture;
  return {
    sky,
    environment: environmentTarget.texture,
    dispose: () => {
      scene.remove(sky);
      if (scene.environment === environmentTarget.texture) scene.environment = null;
      environmentTarget.dispose();
      sky.geometry.dispose();
      sky.material.dispose();
      pmrem.dispose();
    },
  };
}

export class BunchaSceneService {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(42, 16 / 9, 0.1, 1_000);
  readonly content = new SceneRootManager('ride-content');
  readonly renderer: RendererLike;
  readonly quality: QualityProfile;
  readonly plot: PlotDimensions;

  #theme: EnvironmentThemeDefinition;
  #environmentRoot = new THREE.Group();
  #environmentMaterials: EnvironmentMaterialSet;
  #environmentGeometry: SharedEnvironmentGeometry;
  #skyResources: SkyResources | null = null;
  #lights = new THREE.Group();
  #disposed = false;

  constructor(options: BunchaSceneOptions) {
    this.quality = getQualityProfile(options.qualityTier ?? 'high');
    this.plot = Object.freeze({ ...(options.plot ?? DEFAULT_PLOT_DIMENSIONS) });
    this.#theme = getEnvironmentTheme(options.themeId ?? DEFAULT_ENVIRONMENT_THEME_ID);
    const rendererFactory =
      options.rendererFactory ?? ((parameters) => new THREE.WebGLRenderer(parameters));
    this.renderer = rendererFactory({
      canvas: options.canvas,
      antialias: this.quality.tier !== 'low',
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = this.quality.shadows;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    this.camera.position.set(
      this.plot.width * 0.8,
      Math.max(54, this.plot.depth * 0.82),
      this.plot.depth * 1.2,
    );
    this.camera.lookAt(0, 7, 0);
    this.scene.add(this.content.host);
    this.#environmentMaterials = createEnvironmentMaterials(this.#theme);
    this.#environmentGeometry = createSharedEnvironmentGeometry(this.quality, this.plot);
    this.#installTheme(this.#theme);
  }

  get theme(): EnvironmentThemeDefinition {
    return this.#theme;
  }

  get environmentRoot(): THREE.Group {
    return this.#environmentRoot;
  }

  setTheme(id: EnvironmentThemeId): void {
    this.#assertActive();
    if (id === this.#theme.id) return;
    this.#uninstallEnvironment();
    this.#theme = getEnvironmentTheme(id);
    this.#environmentMaterials = createEnvironmentMaterials(this.#theme);
    this.#installTheme(this.#theme);
  }

  replaceContentRoot(nextRoot: THREE.Group, options?: ReplaceRootOptions): THREE.Group {
    this.#assertActive();
    return this.content.replaceRoot(nextRoot, options);
  }

  resize(width: number, height: number, devicePixelRatio = globalThis.devicePixelRatio ?? 1): void {
    this.#assertActive();
    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));
    this.camera.aspect = safeWidth / safeHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(clampPixelRatio(devicePixelRatio, this.quality));
    this.renderer.setSize(safeWidth, safeHeight, false);
  }

  render(): void {
    this.#assertActive();
    this.renderer.render(this.scene, this.camera);
  }

  dispose(options: { readonly disposeContent?: boolean } = {}): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#uninstallEnvironment();
    disposeSharedEnvironmentGeometry(this.#environmentGeometry);
    if (options.disposeContent) {
      const empty = new THREE.Group();
      this.content.replaceRoot(empty, { disposePrevious: true });
    }
    this.scene.remove(this.content.host);
    this.renderer.dispose();
  }

  #installTheme(theme: EnvironmentThemeDefinition): void {
    this.scene.background = new THREE.Color(theme.colors.background);
    this.scene.environmentIntensity = 0.3;
    this.scene.fog = theme.fog
      ? new THREE.Fog(theme.colors.fog, theme.fog.near, theme.fog.far)
      : null;
    this.renderer.toneMappingExposure = theme.lighting.exposure;
    this.#environmentRoot = createProceduralEnvironment({
      theme,
      quality: this.quality,
      materials: this.#environmentMaterials,
      geometries: this.#environmentGeometry,
      plot: this.plot,
    });
    this.scene.add(this.#environmentRoot);
    this.#lights = this.#createLights(theme);
    this.scene.add(this.#lights);
    if (this.renderer instanceof THREE.WebGLRenderer) {
      this.#skyResources = createSkyResources(this.renderer, this.scene, theme);
    }
  }

  #createLights(theme: EnvironmentThemeDefinition): THREE.Group {
    const root = new THREE.Group();
    root.name = `lights:${theme.id}`;
    const hemisphere = new THREE.HemisphereLight(
      theme.lighting.hemisphereSky,
      theme.lighting.hemisphereGround,
      theme.lighting.hemisphereIntensity,
    );
    hemisphere.name = 'hemisphere';
    const sun = new THREE.DirectionalLight(theme.lighting.sunColor, theme.lighting.sunIntensity);
    sun.name = 'sun';
    sun.position.set(25, 48, 18);
    sun.castShadow = this.quality.shadows;
    sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    sun.shadow.camera.left = -66;
    sun.shadow.camera.right = 66;
    sun.shadow.camera.top = 52;
    sun.shadow.camera.bottom = -52;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 110;
    sun.shadow.bias = -0.00035;
    sun.shadow.normalBias = 0.035;
    sun.shadow.radius = 2.4;
    const fill = new THREE.DirectionalLight(theme.lighting.fillColor, theme.lighting.fillIntensity);
    fill.name = 'fill';
    fill.position.set(-24, 18, -20);
    fill.castShadow = false;
    root.add(hemisphere, sun, fill);
    return root;
  }

  #uninstallEnvironment(): void {
    this.scene.remove(this.#environmentRoot, this.#lights);
    this.#skyResources?.dispose();
    this.#skyResources = null;
    disposeMaterialSet(this.#environmentMaterials);
    this.scene.fog = null;
  }

  #assertActive(): void {
    if (this.#disposed) throw new Error('BunchaSceneService has been disposed.');
  }
}
